import { c as _c } from "react/compiler-runtime";
import React, { useSyncExternalStore } from "react";
import { cn } from "@/lib/utils";

/**
 * Universal keyboard-shortcut display: each key renders as its own keycap,
 * and a keycap lights up while its key is physically held so the user can
 * see the combo arming as they try it.
 *
 * All mounted instances share one window-level listener set. Listeners run
 * in the capture phase so no intermediate stopPropagation can starve them,
 * and every key/pointer event reconciles modifier state from the flags it
 * carries (a plain "Meta keydown" listener misses presses it never observes:
 * key already down when the listener attaches, pressed while focus is in
 * browser chrome or another window, event stopped before it bubbles).
 * Window blur clears everything so a missed keyup cannot leave a cap stuck
 * lit; releasing Cmd also clears held character keys because macOS suppresses
 * their keyups while Cmd is down.
 */

interface PressedSnapshot {
  meta: boolean;
  ctrl: boolean;
  shift: boolean;
  alt: boolean;
  /** Physical codes of held non-modifier keys, e.g. "KeyK", "Period". */
  codes: ReadonlySet<string>;
  /** Lowercased `key` values captured at keydown, e.g. "k", "enter". */
  chars: ReadonlySet<string>;
}
const EMPTY_SET: ReadonlySet<string> = new Set();
const EMPTY: PressedSnapshot = {
  meta: false,
  ctrl: false,
  shift: false,
  alt: false,
  codes: EMPTY_SET,
  chars: EMPTY_SET
};
const MODIFIER_KEYS = new Set(["Meta", "Control", "Shift", "Alt"]);
function isEditableTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target.isContentEditable);
}
let snapshot = EMPTY;
const subscribers = new Set<() => void>();
/** code (or key when code is empty) -> lowercased key at keydown. */
const heldKeys = new Map<string, string>();
function rebuildSnapshot(mods: {
  meta: boolean;
  ctrl: boolean;
  shift: boolean;
  alt: boolean;
}) {
  const codes = new Set(heldKeys.keys());
  const chars = new Set(heldKeys.values());
  const unchanged = mods.meta === snapshot.meta && mods.ctrl === snapshot.ctrl && mods.shift === snapshot.shift && mods.alt === snapshot.alt && codes.size === snapshot.codes.size && chars.size === snapshot.chars.size && [...codes].every(c => snapshot.codes.has(c)) && [...chars].every(c => snapshot.chars.has(c));
  if (unchanged) return;
  snapshot = {
    ...mods,
    codes,
    chars
  };
  subscribers.forEach(notify => notify());
}

// Shift without Alt/Cmd/Ctrl while an editable element is focused is typing
// (capital letters), not a chord arming. Every reconcile path must apply
// this: onKeyDown skips such keystrokes, but the letter's keyup and pointer
// moves still reconcile, and adopting their raw shiftKey would light ⇧
// anyway. Keyboard events target the focused element; pointer events target
// whatever is under the cursor, so they check activeElement instead.
function effectiveShift(e: KeyboardEvent | MouseEvent): boolean {
  if (!e.shiftKey) return false;
  if (e.altKey || e.metaKey || e.ctrlKey) return true;
  const focused = e instanceof KeyboardEvent ? e.target : document.activeElement;
  return !isEditableTarget(focused);
}
function reconcile(e: KeyboardEvent | MouseEvent) {
  // macOS never delivers keyups for character keys released while Cmd is
  // held, so dropping Cmd is the only reliable signal to unstick them.
  if (snapshot.meta && !e.metaKey) heldKeys.clear();
  rebuildSnapshot({
    meta: e.metaKey,
    ctrl: e.ctrlKey,
    shift: effectiveShift(e),
    alt: e.altKey
  });
}
function onKeyDown(e: KeyboardEvent) {
  // Keystrokes going into an input/textarea/contenteditable without
  // Alt/Cmd/Ctrl are text entry, not a shortcut being armed, so they don't
  // light keycaps. Chords carrying those modifiers still track, since global
  // shortcuts fire even while the composer is focused.
  if (!e.altKey && !e.metaKey && !e.ctrlKey && isEditableTarget(e.target)) {
    return;
  }
  if (!MODIFIER_KEYS.has(e.key)) {
    heldKeys.set(e.code || e.key, e.key.toLowerCase());
  }
  reconcile(e);
}
function onKeyUp(e: KeyboardEvent) {
  heldKeys.delete(e.code || e.key);
  reconcile(e);
}
function onBlur() {
  heldKeys.clear();
  rebuildSnapshot({
    meta: false,
    ctrl: false,
    shift: false,
    alt: false
  });
}

// Pointer events can only change modifier flags, never heldKeys, so the
// hot pointermove path skips reconcile's Set allocation when the flags
// already match (the overwhelmingly common case while the mouse moves).
function onPointer(e: MouseEvent) {
  if (e.metaKey === snapshot.meta && e.ctrlKey === snapshot.ctrl && effectiveShift(e) === snapshot.shift && e.altKey === snapshot.alt) {
    return;
  }
  reconcile(e);
}
function attach() {
  window.addEventListener("keydown", onKeyDown, true);
  window.addEventListener("keyup", onKeyUp, true);
  window.addEventListener("pointerdown", onPointer, true);
  window.addEventListener("pointermove", onPointer, true);
  window.addEventListener("blur", onBlur);
}
function detach() {
  window.removeEventListener("keydown", onKeyDown, true);
  window.removeEventListener("keyup", onKeyUp, true);
  window.removeEventListener("pointerdown", onPointer, true);
  window.removeEventListener("pointermove", onPointer, true);
  window.removeEventListener("blur", onBlur);
  heldKeys.clear();
  snapshot = EMPTY;
}
function subscribe(notify: () => void) {
  if (subscribers.size === 0) attach();
  subscribers.add(notify);
  return () => {
    subscribers.delete(notify);
    if (subscribers.size === 0) detach();
  };
}
const noopSubscribe = () => () => {};
const getSnapshot = () => snapshot;
const getStaticSnapshot = () => EMPTY;

/** Live physical keyboard state, shared across all subscribers. */
export function usePressedKeys(t0) {
  const enabled = t0 === undefined ? true : t0;
  return useSyncExternalStore(enabled ? subscribe : noopSubscribe, enabled ? getSnapshot : getStaticSnapshot, getStaticSnapshot);
}
const getModifierHeld = () => snapshot.meta || snapshot.ctrl;
const getStaticModifierHeld = () => false;

/**
 * True while Cmd or Ctrl is physically held. Derives a boolean from the
 * shared store so large consumers re-render only on modifier flips, not on
 * every tracked keystroke.
 */
export function useModifierHeld(t0) {
  const enabled = t0 === undefined ? true : t0;
  return useSyncExternalStore(enabled ? subscribe : noopSubscribe, enabled ? getModifierHeld : getStaticModifierHeld, getStaticModifierHeld);
}
const ARROW_CHARS: Record<string, string> = {
  "↑": "arrowup",
  "↓": "arrowdown",
  "←": "arrowleft",
  "→": "arrowright"
};
const PUNCTUATION_CODES: Record<string, string> = {
  ".": "Period",
  ",": "Comma",
  "/": "Slash",
  "?": "Slash",
  ";": "Semicolon",
  "'": "Quote",
  "[": "BracketLeft",
  "]": "BracketRight",
  "-": "Minus",
  "=": "Equal",
  "`": "Backquote"
};

/**
 * Whether the key a display token names is physically held. Tokens are the
 * strings shortcut UIs render: platform glyphs ("⌘", "⇧", "↵"), key words
 * ("Cmd", "Shift", "Enter"), characters ("K", "."), alternatives ("J/K",
 * "⌥/⌘"), and double-tap multipliers ("⌥ ×2").
 */
export function isTokenPressed(token: string, pressed: PressedSnapshot): boolean {
  const t = token.trim();
  const base = t.replace(/\s*×\d+$/, "");
  if (base !== t) return isTokenPressed(base, pressed);
  if (t.length > 1 && t.includes("/")) {
    return t.split("/").some(part => isTokenPressed(part, pressed));
  }
  switch (t) {
    // Cmd and Ctrl light together: they are the same platform-specific
    // modifier slot, and handlers accept either (e.metaKey || e.ctrlKey).
    case "⌘":
    case "Cmd":
    case "Ctrl":
    case "Control":
      return pressed.meta || pressed.ctrl;
    case "⌃":
      return pressed.ctrl;
    case "⇧":
    case "Shift":
      return pressed.shift;
    case "⌥":
    case "Alt":
    case "Option":
      return pressed.alt;
    case "↵":
    case "⏎":
    case "Enter":
    case "Return":
      return pressed.chars.has("enter");
    case "Esc":
    case "esc":
    case "⎋":
    case "Escape":
      return pressed.chars.has("escape");
    case "Space":
    case "␣":
      return pressed.chars.has(" ");
  }
  const glyphs = [...t];
  if (glyphs.every(g => g in ARROW_CHARS)) {
    return glyphs.some(g => pressed.chars.has(ARROW_CHARS[g]));
  }
  const lower = t.toLowerCase();
  if (pressed.chars.has(lower)) return true;
  if (/^[a-z]$/.test(lower)) return pressed.codes.has(`Key${t.toUpperCase()}`);
  if (/^[0-9]$/.test(t)) return pressed.codes.has(`Digit${t}`);
  const code = PUNCTUATION_CODES[t];
  return code ? pressed.codes.has(code) : false;
}

/**
 * Single keycap. `held` inverts the cap while its key is physically pressed;
 * exposed as `data-held` for styling and tests.
 */
export function ShortcutKbd(t0) {
  const $ = _c(7);
  const {
    children,
    held: t1,
    className
  } = t0;
  const held = t1 === undefined ? false : t1;
  const t2 = held || undefined;
  const t3 = held ? "border-neutral-800 bg-neutral-800 text-white dark:border-white dark:bg-white dark:text-neutral-900" : "border-black/5 bg-neutral-100/60 text-neutral-400 dark:border-white/10 dark:bg-neutral-700/50 dark:text-neutral-500";
  let t4;
  if ($[0] !== className || $[1] !== t3) {
    t4 = cn("inline-flex h-[18px] min-w-[18px] select-none items-center justify-center rounded border px-1 font-mono text-[10px] font-medium leading-none transition-colors duration-100", t3, className);
    $[0] = className;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== children || $[4] !== t2 || $[5] !== t4) {
    t5 = <kbd data-held={t2} className={t4}>{children}</kbd>;
    $[3] = children;
    $[4] = t2;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
}
export interface ShortcutKeysProps {
  /** Display tokens, one keycap each, e.g. ["⌘", "⇧", "."]. */
  keys: string[];
  /**
   * Pins every key but the last in the held style, previewing the combo
   * arming without real key events (Storybook, pinned menus).
   */
  forceModifiersHeld?: boolean;
  className?: string;
  /** Extra classes per keycap, e.g. contrast overrides on busy surfaces. */
  kbdClassName?: string;
}

/**
 * Renders a shortcut combo as keycaps that light up while physically held,
 * so the user can watch the shortcut arm as they try it.
 */
export function ShortcutKeys(t0) {
  const $ = _c(15);
  const {
    keys,
    forceModifiersHeld: t1,
    className,
    kbdClassName
  } = t0;
  const forceModifiersHeld = t1 === undefined ? false : t1;
  const pressed = usePressedKeys();
  let t2;
  if ($[0] !== className) {
    t2 = cn("inline-flex items-center gap-0.5", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== forceModifiersHeld || $[3] !== kbdClassName || $[4] !== keys || $[5] !== pressed) {
    let t4;
    if ($[7] !== forceModifiersHeld || $[8] !== kbdClassName || $[9] !== keys.length || $[10] !== pressed) {
      t4 = (key, idx) => <ShortcutKbd key={`${key}-${idx}`} className={kbdClassName} held={isTokenPressed(key, pressed) || forceModifiersHeld && idx < keys.length - 1}>{key}</ShortcutKbd>;
      $[7] = forceModifiersHeld;
      $[8] = kbdClassName;
      $[9] = keys.length;
      $[10] = pressed;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    t3 = keys.map(t4);
    $[2] = forceModifiersHeld;
    $[3] = kbdClassName;
    $[4] = keys;
    $[5] = pressed;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[12] !== t2 || $[13] !== t3) {
    t4 = <span className={t2}>{t3}</span>;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}